import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import PageMeta from "../components/common/PageMeta";
import Button from "../components/ui/button/Button";
import Input from "../components/form/input/InputField";
import Label from "../components/form/Label";
import Checkbox from "../components/form/input/Checkbox";
import Switch from "../components/form/switch/Switch";
import { buildingService, Building, SiteManager, FloorRepresentative } from "../services/buildingService";
import { userService, TransferableUser } from "../services/userService";
import { useBuilding } from "../context/BuildingContext";
import { useAuth } from "../context/AuthContext";

const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const US_STATES = [
  { value: "AL", label: "AL - Alabama" }, { value: "AK", label: "AK - Alaska" },
  { value: "AZ", label: "AZ - Arizona" }, { value: "AR", label: "AR - Arkansas" },
  { value: "CA", label: "CA - California" }, { value: "CO", label: "CO - Colorado" },
  { value: "CT", label: "CT - Connecticut" }, { value: "DE", label: "DE - Delaware" },
  { value: "DC", label: "DC - District of Columbia" }, { value: "FL", label: "FL - Florida" },
  { value: "GA", label: "GA - Georgia" }, { value: "HI", label: "HI - Hawaii" },
  { value: "ID", label: "ID - Idaho" }, { value: "IL", label: "IL - Illinois" },
  { value: "IN", label: "IN - Indiana" }, { value: "IA", label: "IA - Iowa" },
  { value: "KS", label: "KS - Kansas" }, { value: "KY", label: "KY - Kentucky" },
  { value: "LA", label: "LA - Louisiana" }, { value: "ME", label: "ME - Maine" },
  { value: "MD", label: "MD - Maryland" }, { value: "MA", label: "MA - Massachusetts" },
  { value: "MI", label: "MI - Michigan" }, { value: "MN", label: "MN - Minnesota" },
  { value: "MS", label: "MS - Mississippi" }, { value: "MO", label: "MO - Missouri" },
  { value: "MT", label: "MT - Montana" }, { value: "NE", label: "NE - Nebraska" },
  { value: "NV", label: "NV - Nevada" }, { value: "NH", label: "NH - New Hampshire" },
  { value: "NJ", label: "NJ - New Jersey" }, { value: "NM", label: "NM - New Mexico" },
  { value: "NY", label: "NY - New York" }, { value: "NC", label: "NC - North Carolina" },
  { value: "ND", label: "ND - North Dakota" }, { value: "OH", label: "OH - Ohio" },
  { value: "OK", label: "OK - Oklahoma" }, { value: "OR", label: "OR - Oregon" },
  { value: "PA", label: "PA - Pennsylvania" }, { value: "RI", label: "RI - Rhode Island" },
  { value: "SC", label: "SC - South Carolina" }, { value: "SD", label: "SD - South Dakota" },
  { value: "TN", label: "TN - Tennessee" }, { value: "TX", label: "TX - Texas" },
  { value: "UT", label: "UT - Utah" }, { value: "VT", label: "VT - Vermont" },
  { value: "VA", label: "VA - Virginia" }, { value: "WA", label: "WA - Washington" },
  { value: "WV", label: "WV - West Virginia" }, { value: "WI", label: "WI - Wisconsin" },
  { value: "WY", label: "WY - Wyoming" },
];

export default function BuildingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBuilding, selectBuilding, refreshBuildings } = useBuilding();
  const [building, setBuilding] = useState<Building | null>(null);
  const [siteManagers, setSiteManagers] = useState<SiteManager[]>([]);
  const [floorReps, setFloorReps] = useState<FloorRepresentative[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveConfirmText, setArchiveConfirmText] = useState("");
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteType, setInviteType] = useState<1 | 2>(1); // 1 = Site Manager, 2 = Floor Rep
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  // Transfer tab state
  const [modalTab, setModalTab] = useState<'invite' | 'transfer'>('invite');
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<TransferableUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [transferNote, setTransferNote] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);

  // Action dropdown
  const [activeDropdownId, setActiveDropdownId] = useState<number | null>(null);

  // Edit user modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<SiteManager | FloorRepresentative | null>(null);
  const [editFormData, setEditFormData] = useState({ name: '', username: '', email: '', user_type: 0 });
  const [resetPassword, setResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('12345678');
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [editUserError, setEditUserError] = useState<string | null>(null);

  // Archive user modal state
  const [showArchiveUserModal, setShowArchiveUserModal] = useState(false);
  const [archivingUser, setArchivingUser] = useState<SiteManager | FloorRepresentative | null>(null);
  const [archiveUserConfirmText, setArchiveUserConfirmText] = useState("");
  const [isArchivingUser, setIsArchivingUser] = useState(false);
  const [archiveUserError, setArchiveUserError] = useState<string | null>(null);
  const [archiveUserSuccess, setArchiveUserSuccess] = useState(false);

  // Transfer individual user modal state
  const [showUserTransferModal, setShowUserTransferModal] = useState(false);
  const [transferringUser, setTransferringUser] = useState<SiteManager | FloorRepresentative | null>(null);
  const [allBuildings, setAllBuildings] = useState<Building[]>([]);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [individualTransferNote, setIndividualTransferNote] = useState('');
  const [isIndividualTransferring, setIsIndividualTransferring] = useState(false);
  const [individualTransferError, setIndividualTransferError] = useState<string | null>(null);
  const [individualTransferSuccess, setIndividualTransferSuccess] = useState<string | null>(null);

  const isAdmin = user?.user_type === 3;

  const [formData, setFormData] = useState({
    name: "",
    street_number: "",
    street_name: "",
    city: "",
    state: "",
    zip: "",
    email: "",
    phone_number: "",
    require_escort_photo: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setIsLoading(true);
      setError(null);
      try {
        const [buildingData, managersData, repsData] = await Promise.all([
          buildingService.getBuilding(parseInt(id)),
          buildingService.getSiteManagers(parseInt(id)),
          buildingService.getFloorReps(parseInt(id)),
        ]);
        setBuilding(buildingData);
        setSiteManagers(managersData);
        setFloorReps(repsData);

        // Initialize form data
        setFormData({
          name: buildingData.name || "",
          street_number: String(buildingData.street_number || ""),
          street_name: buildingData.street_name || "",
          city: buildingData.city || "",
          state: buildingData.state || "",
          zip: String(buildingData.zip || ""),
          email: buildingData.email || "",
          phone_number: buildingData.phone ? formatPhoneNumber(buildingData.phone) : "",
            require_escort_photo: !!buildingData.require_escort_photo,
        });

        // Sync building context if it differs from route or is not set
        if (!currentBuilding || currentBuilding.id !== buildingData.id) {
          selectBuilding(buildingData);
        }
      } catch (err) {
        console.error("Error fetching building data:", err);
        setError("Failed to load building data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleInputChange = (field: string, value: string) => {
    const processed = field === 'phone_number' ? formatPhoneNumber(value) : value;
    setFormData((prev) => ({ ...prev, [field]: processed }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!building || !id) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    if (!formData.email) {
      setSaveError("Building email is required");
      setIsSaving(false);
      return;
    }
    if (!formData.phone_number) {
      setSaveError("Phone number is required");
      setIsSaving(false);
      return;
    }
    const digitCount = formData.phone_number.replace(/\D/g, '').length;
    if (digitCount !== 10) {
      setSaveError("Phone number must be exactly 10 digits");
      setIsSaving(false);
      return;
    }

    try {
      const updatedBuilding = await buildingService.updateBuilding(parseInt(id), {
        name: formData.name,
        street_number: String(formData.street_number),
        street_name: formData.street_name,
        city: formData.city,
        state: formData.state,
        zip: String(formData.zip),
        email: formData.email,
        phone_number: formData.phone_number,
        require_escort_photo: formData.require_escort_photo,
      });

      setBuilding(updatedBuilding);
      // Update form data with the response so the detail view shows fresh values
      setFormData({
        name: updatedBuilding.name || "",
        street_number: String(updatedBuilding.street_number || ""),
        street_name: updatedBuilding.street_name || "",
        city: updatedBuilding.city || "",
        state: updatedBuilding.state || "",
        zip: String(updatedBuilding.zip || ""),
        email: updatedBuilding.email || "",
        phone_number: updatedBuilding.phone ? formatPhoneNumber(updatedBuilding.phone) : "",
          require_escort_photo: !!updatedBuilding.require_escort_photo,
      });
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (building) {
      setFormData({
        name: building.name || "",
        street_number: building.street_number || "",
        street_name: building.street_name || "",
        city: building.city || "",
        state: building.state || "",
        zip: building.zip || "",
        email: building.email || "",
        phone_number: building.phone ? formatPhoneNumber(building.phone) : "",
        require_escort_photo: !!building.require_escort_photo,
      });
    }
    setIsEditing(false);
    setSaveError(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      setSaveError('Please select a JPEG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSaveError('Image must be less than 5MB');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setSaveError(null);
  };

  const handleImageUpload = async () => {
    if (!imageFile || !building || !id) return;

    setIsUploadingImage(true);
    setSaveError(null);

    try {
      const updatedBuilding = await buildingService.uploadBuildingImage(parseInt(id), imageFile);
      setBuilding(updatedBuilding);
      setImageFile(null);
      setImagePreview(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const cancelImageSelect = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  };

  const handleArchive = async () => {
    if (!building || !id || archiveConfirmText !== "ARCHIVE") return;

    setIsArchiving(true);
    setArchiveError(null);

    try {
      await buildingService.archiveBuilding(parseInt(id));
      await refreshBuildings();
      setShowArchiveModal(false);
      navigate("/sites");
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : "Failed to archive site");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleActivate = async () => {
    if (!building || !id) return;

    setIsArchiving(true);
    setArchiveError(null);

    try {
      const updatedBuilding = await buildingService.activateBuilding(parseInt(id));
      setBuilding(updatedBuilding);
      selectBuilding(updatedBuilding);
      await refreshBuildings();
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : "Failed to activate site");
    } finally {
      setIsArchiving(false);
    }
  };

  const openInviteModal = (type: 1 | 2) => {
    setInviteType(type);
    setInviteEmail("");
    setInviteName("");
    setInviteError(null);
    setInviteCode(null);
    setModalTab('invite');
    setSearchKeyword("");
    setSearchResults([]);
    setTransferNote("");
    setTransferError(null);
    setTransferSuccess(null);
    setShowInviteModal(true);
  };

  const isSbmEmail = (email: string) => email.toLowerCase().endsWith("@sbmnyc.com");

  const handleInvite = async () => {
    if (!building || !id || !inviteName) return;

    // Site Managers require email, Floor Reps use building email
    const emailToUse = inviteType === 1 ? inviteEmail : building.email;

    // Site Managers must have @sbmnyc.com email
    if (inviteType === 1) {
      if (!inviteEmail) {
        setInviteError("Email is required for Site Managers");
        return;
      }
      if (!isSbmEmail(inviteEmail)) {
        setInviteError("Site Managers must use an @sbmnyc.com email address");
        return;
      }
    }

    // Floor Reps require building to have an email
    if (inviteType === 2 && !building.email) {
      setInviteError("This building doesn't have an email address configured. Please add one in the site settings first.");
      return;
    }

    setIsInviting(true);
    setInviteError(null);

    try {
      const result = await userService.inviteUser({
        building_id: parseInt(id),
        email: emailToUse || "",
        name: inviteName,
        user_type: inviteType,
      });
      setInviteCode(result.invite_code);

      // Refresh the users list
      const [managersData, repsData] = await Promise.all([
        buildingService.getSiteManagers(parseInt(id)),
        buildingService.getFloorReps(parseInt(id)),
      ]);
      setSiteManagers(managersData);
      setFloorReps(repsData);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to send invite");
    } finally {
      setIsInviting(false);
    }
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail("");
    setInviteName("");
    setInviteError(null);
    setInviteCode(null);
    setSearchKeyword("");
    setSearchResults([]);
    setTransferNote("");
    setTransferError(null);
    setTransferSuccess(null);
  };

  // Debounced search for transferable users
  useEffect(() => {
    if (modalTab !== 'transfer' || !searchKeyword.trim() || !id) {
      if (modalTab === 'transfer' && !searchKeyword.trim()) {
        setSearchResults([]);
      }
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      setTransferError(null);
      try {
        const results = await userService.searchTransferable(
          parseInt(id),
          inviteType,
          searchKeyword.trim(),
        );
        setSearchResults(results);
      } catch (err) {
        setTransferError(err instanceof Error ? err.message : "Failed to search users");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchKeyword, modalTab, inviteType, id]);

  // Close dropdown on click outside
  useEffect(() => {
    if (activeDropdownId === null) return;
    const handleClickOutside = () => setActiveDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdownId]);

  const handleTransfer = async (userId: number, userName: string) => {
    if (!id) return;

    setIsTransferring(true);
    setTransferError(null);
    setTransferSuccess(null);

    try {
      await userService.transferUser({
        user_id: userId,
        building_id: parseInt(id),
        note: transferNote || undefined,
      });

      setTransferSuccess(`${userName} has been transferred to this building.`);
      setSearchKeyword("");
      setSearchResults([]);
      setTransferNote("");

      // Refresh the users list
      const [managersData, repsData] = await Promise.all([
        buildingService.getSiteManagers(parseInt(id)),
        buildingService.getFloorReps(parseInt(id)),
      ]);
      setSiteManagers(managersData);
      setFloorReps(repsData);
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : "Failed to transfer user");
    } finally {
      setIsTransferring(false);
    }
  };

  // Open edit modal for a user
  const openEditModal = (user: SiteManager | FloorRepresentative) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      username: user.username || '',
      email: user.email || '',
      user_type: user.user_type,
    });
    setEditUserError(null);
    setResetPassword(false);
    setNewPassword('12345678');
    setShowEditModal(true);
    setActiveDropdownId(null);
  };

  const handleEditSave = async () => {
    if (!editingUser || !id) return;

    const roleChanged = editFormData.user_type !== editingUser.user_type;

    // Validate credential fields based on the target role
    if (roleChanged) {
      if (editFormData.user_type === 1) {
        if (!editFormData.email) {
          setEditUserError('Email is required when promoting to Site Manager');
          return;
        }
        if (!isSbmEmail(editFormData.email)) {
          setEditUserError('Site Managers must use an @sbmnyc.com email address');
          return;
        }
      } else if (editFormData.user_type === 2) {
        if (!editFormData.username) {
          setEditUserError('Username is required when changing to Floor Representative');
          return;
        }
      }
    } else {
      // Validate email for existing Site Managers
      if (editFormData.user_type === 1 && editFormData.email && !isSbmEmail(editFormData.email)) {
        setEditUserError('Site Managers must use an @sbmnyc.com email address');
        return;
      }
    }

    setIsUpdatingUser(true);
    setEditUserError(null);

    try {
      // Handle role change first
      if (roleChanged) {
        const roleData: { user_id: number; new_type: 1 | 2; email?: string; username?: string } = {
          user_id: editingUser.id,
          new_type: editFormData.user_type as 1 | 2,
        };
        if (editFormData.user_type === 1) {
          roleData.email = editFormData.email;
        } else {
          roleData.username = editFormData.username;
        }
        await userService.changeUserRole(roleData);
      }

      // Handle name update (and other fields if role didn't change)
      const updateData: Record<string, string | number> = {};
      if (editFormData.name && editFormData.name !== editingUser.name) updateData.name = editFormData.name;

      if (!roleChanged) {
        if (editFormData.email !== (editingUser.email || '')) updateData.email = editFormData.email;
        if (editFormData.username && editFormData.username !== (editingUser.username || '')) updateData.username = editFormData.username;
      }

      if (resetPassword && newPassword) {
        updateData.send_new_password = 1;
        updateData.password = newPassword;
      }

      if (Object.keys(updateData).length > 0) {
        await userService.updateUser(editingUser.id, updateData);
      }

      // Refresh lists
      const [managersData, repsData] = await Promise.all([
        buildingService.getSiteManagers(parseInt(id)),
        buildingService.getFloorReps(parseInt(id)),
      ]);
      setSiteManagers(managersData);
      setFloorReps(repsData);

      setShowEditModal(false);
    } catch (err) {
      setEditUserError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setIsUpdatingUser(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditUserError(null);
    setResetPassword(false);
    setNewPassword('12345678');
  };

  // Transfer individual user to a different building
  const openUserTransferModal = async (user: SiteManager | FloorRepresentative) => {
    setTransferringUser(user);
    setBuildingSearch('');
    setIndividualTransferNote('');
    setIndividualTransferError(null);
    setIndividualTransferSuccess(null);
    setShowUserTransferModal(true);
    setActiveDropdownId(null);

    // Load all buildings
    try {
      const buildings = await buildingService.getBuildings();
      // Filter out current building
      setAllBuildings(buildings.filter(b => b.id !== parseInt(id!) && b.status === 1));
    } catch {
      setIndividualTransferError('Failed to load buildings');
    }
  };

  const handleIndividualTransfer = async (targetBuildingId: number, targetBuildingName: string) => {
    if (!transferringUser || !id) return;

    setIsIndividualTransferring(true);
    setIndividualTransferError(null);

    try {
      await userService.transferUser({
        user_id: transferringUser.id,
        building_id: targetBuildingId,
        note: individualTransferNote || undefined,
      });

      setIndividualTransferSuccess(`${transferringUser.name} has been transferred to ${targetBuildingName}.`);

      // Refresh lists
      const [managersData, repsData] = await Promise.all([
        buildingService.getSiteManagers(parseInt(id)),
        buildingService.getFloorReps(parseInt(id)),
      ]);
      setSiteManagers(managersData);
      setFloorReps(repsData);
    } catch (err) {
      setIndividualTransferError(err instanceof Error ? err.message : 'Failed to transfer user');
    } finally {
      setIsIndividualTransferring(false);
    }
  };

  const closeUserTransferModal = () => {
    setShowUserTransferModal(false);
    setTransferringUser(null);
    setIndividualTransferError(null);
    setIndividualTransferSuccess(null);
  };

  // Archive user
  const openArchiveUserModal = (user: SiteManager | FloorRepresentative) => {
    setArchivingUser(user);
    setArchiveUserConfirmText("");
    setArchiveUserError(null);
    setShowArchiveUserModal(true);
    setActiveDropdownId(null);
  };

  const handleArchiveUser = async () => {
    if (!archivingUser || !id || archiveUserConfirmText !== "ARCHIVE") return;

    setIsArchivingUser(true);
    setArchiveUserError(null);

    try {
      await userService.updateUserStatus(archivingUser.id, 0);

      // Refresh lists
      const [managersData, repsData] = await Promise.all([
        buildingService.getSiteManagers(parseInt(id)),
        buildingService.getFloorReps(parseInt(id)),
      ]);
      setSiteManagers(managersData);
      setFloorReps(repsData);

      setShowArchiveUserModal(false);
      setArchiveUserSuccess(true);
      setTimeout(() => setArchiveUserSuccess(false), 3000);
    } catch (err) {
      setArchiveUserError(err instanceof Error ? err.message : "Failed to archive user");
    } finally {
      setIsArchivingUser(false);
    }
  };

  const closeArchiveUserModal = () => {
    setShowArchiveUserModal(false);
    setArchivingUser(null);
    setArchiveUserConfirmText("");
    setArchiveUserError(null);
  };

  const filteredBuildings = allBuildings.filter(b =>
    !buildingSearch.trim() || b.name.toLowerCase().includes(buildingSearch.toLowerCase())
  );

  // Filter out archived users (user_status !== 1)
  const activeSiteManagers = siteManagers.filter(m => m.user_status === 1);
  const activeFloorReps = floorReps.filter(r => r.user_status === 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!building) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-gray-500 dark:text-gray-400">Building not found</p>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title={`${building.name} | SBM Package Admin`}
        description={`Building details for ${building.name}`}
      />

      {/* Archived Status Banner */}
      {building.status === 0 && (
        <div className="mb-6 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <span className="text-sm font-medium text-amber-700">This site is archived</span>
          </div>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleActivate}
              disabled={isArchiving}
            >
              {isArchiving ? "Activating..." : "Activate Site"}
            </Button>
          )}
        </div>
      )}

      {/* Success Toast */}
      {saveSuccess && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in-out rounded-lg bg-green-600 px-5 py-3 text-sm font-medium text-white shadow-lg">
          Building details updated successfully!
        </div>
      )}

      {/* Archive User Success Toast */}
      {archiveUserSuccess && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in-out rounded-lg bg-green-600 px-5 py-3 text-sm font-medium text-white shadow-lg">
          User archived successfully
        </div>
      )}

      {/* Archive Error */}
      {archiveError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          {archiveError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left Column - Building Info */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="relative">
            <div className="flex items-center justify-between p-5">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {isEditing ? "Edit Building" : building.name}
              </h2>
              {!isEditing && (
                <button
                  type="button"
                  aria-label="Edit"
                  title="Edit"
                  onClick={() => setIsEditing(true)}
                  className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>
            {!isEditing && (
              <div className="group relative px-5">
                <img
                  src={imagePreview || building.image_url || "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800"}
                  alt={building.name}
                  className="aspect-video w-full rounded-lg object-cover"
                />
                {/* Image upload overlay */}
                {!imageFile ? (
                  <label className="absolute inset-x-5 inset-y-0 flex cursor-pointer items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/40">
                    <span className="rounded-lg bg-white/90 px-4 py-2 text-sm font-medium text-gray-700 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                      Change Image
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="absolute inset-x-5 bottom-0 flex items-center justify-between rounded-b-lg bg-black/60 px-4 py-3">
                    <span className="truncate text-sm text-white">{imageFile.name}</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={cancelImageSelect}
                        disabled={isUploadingImage}
                        className="rounded-md bg-white/20 px-3 py-1 text-sm text-white hover:bg-white/30"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleImageUpload}
                        disabled={isUploadingImage}
                        className="rounded-md bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
                      >
                        {isUploadingImage ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {saveError && (
            <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {saveError}
            </div>
          )}

          {isEditing ? (
            <div className="p-5">
              <div className="space-y-4">
                <div>
                  <Label>Building Name <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter building name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Street Number <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      value={formData.street_number}
                      onChange={(e) => handleInputChange("street_number", e.target.value)}
                      placeholder="123"
                    />
                  </div>
                  <div>
                    <Label>Street Name <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      value={formData.street_name}
                      onChange={(e) => handleInputChange("street_name", e.target.value)}
                      placeholder="Main Street"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <Label>State <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <select
                        className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 text-gray-800"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                      >
                        <option value="" disabled>Select state</option>
                        {US_STATES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <svg className="absolute text-gray-700 dark:text-gray-400 right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.79175 8.02075L10.0001 13.2291L15.2084 8.02075" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <Label>Zip <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => handleInputChange("zip", e.target.value)}
                      placeholder="10001"
                    />
                  </div>
                </div>

                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="building@example.com"
                  />
                </div>

                <div>
                  <Label>Phone Number <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={formData.phone_number}
                    onChange={(e) => handleInputChange("phone_number", e.target.value)}
                    placeholder="(212) 555-0100"
                  />
                </div>

                <div>
                  <Switch
                    label="Require a photo of the item for escorts"
                    defaultChecked={formData.require_escort_photo}
                    onChange={(checked) => {
                      setFormData((prev) => ({ ...prev, require_escort_photo: checked }));
                      setSaveError(null);
                      setSaveSuccess(false);
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    When on, iPad staff must photograph what the courier is delivering before saving an escort.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 p-5">
                <div>
                  <label className="text-sm font-medium text-brand-500">Name</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">Street Name</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.street_name || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">Street Number</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.street_number || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">City</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.city || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">State</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.state || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">Zip</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.zip || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">Email</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.email || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">Phone Number</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.phone || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-500">Escort item photo</label>
                  <p className="mt-1 text-gray-800 dark:text-white/90">{building.require_escort_photo ? "Required" : "Optional"}</p>
                </div>
              </div>

              {/* Archive Site Button - Admin only, active sites only */}
              {isAdmin && building.status !== 0 && (
                <div className="border-t border-gray-200 p-5 dark:border-gray-700">
                  <button
                    onClick={() => setShowArchiveModal(true)}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archive Site
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Column - Staff Lists */}
        <div className="space-y-6">
          {/* Site Manager Section */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-gray-800">
              <h3 className="font-semibold text-gray-800 dark:text-white/90">Site Manager</h3>
              <Button size="sm" onClick={() => openInviteModal(1)}>Add New</Button>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {activeSiteManagers.map((manager) => (
                <div key={manager.id} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {manager.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white/90">{manager.name}</p>
                      <p className="text-xs text-gray-500">Site Manager</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {manager.email && (
                      <span className="flex items-center text-sm text-gray-500">
                        <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {manager.email}
                      </span>
                    )}
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveDropdownId(activeDropdownId === manager.id ? null : manager.id); }}
                        className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>
                      {activeDropdownId === manager.id && (
                        <div className="absolute right-0 top-8 z-50 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                          <button
                            onClick={() => openEditModal(manager)}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => openUserTransferModal(manager)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              Transfer
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => openArchiveUserModal(manager)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                              Archive
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {activeSiteManagers.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500">
                  No site managers found
                </div>
              )}
            </div>
          </div>

          {/* Floor Representative Section */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-gray-800">
              <h3 className="font-semibold text-gray-800 dark:text-white/90">Floor Representative</h3>
              <Button size="sm" onClick={() => openInviteModal(2)}>Add New</Button>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {activeFloorReps.map((rep) => (
                <div key={rep.id} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {rep.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white/90">{rep.name}</p>
                      <p className="text-xs text-gray-500">Floor Representative</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {rep.email && (
                      <span className="flex items-center text-sm text-gray-500">
                        <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {rep.email}
                      </span>
                    )}
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveDropdownId(activeDropdownId === rep.id ? null : rep.id); }}
                        className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>
                      {activeDropdownId === rep.id && (
                        <div className="absolute right-0 top-8 z-50 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                          <button
                            onClick={() => openEditModal(rep)}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => openUserTransferModal(rep)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              Transfer
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => openArchiveUserModal(rep)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                              Archive
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {activeFloorReps.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500">
                  No floor representatives found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invite / Transfer Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50">
          <div className="mx-4 flex h-[640px] w-full max-w-md flex-col rounded-2xl bg-white p-6 dark:bg-gray-800">
            {inviteCode ? (
              // Success state - show invite code
              <>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Invitation Sent!</h3>
                </div>

                <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                  An invitation has been sent to <strong>{inviteType === 1 ? inviteEmail : building?.email}</strong>. They can use the following code to join:
                </p>

                <div className="mb-6 rounded-lg bg-gray-100 p-4 text-center dark:bg-gray-700">
                  <p className="text-3xl font-bold tracking-widest text-gray-800 dark:text-white">
                    {inviteCode}
                  </p>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">6-digit invite code</p>
                </div>

                <div className="flex justify-end">
                  <Button onClick={closeInviteModal}>Done</Button>
                </div>
              </>
            ) : transferSuccess ? (
              // Transfer success state
              <>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Transfer Complete</h3>
                </div>

                <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">{transferSuccess}</p>

                <div className="flex justify-end">
                  <Button onClick={closeInviteModal}>Done</Button>
                </div>
              </>
            ) : (
              // Form state with tabs
              <>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
                    <svg className="h-5 w-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Add {inviteType === 1 ? "Site Manager" : "Floor Representative"}
                  </h3>
                </div>

                {/* Tabs */}
                {isAdmin && (
                  <div className="mb-4 flex rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
                    <button
                      onClick={() => { setModalTab('invite'); setTransferError(null); }}
                      className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        modalTab === 'invite'
                          ? 'bg-white text-gray-800 shadow-sm dark:bg-gray-600 dark:text-white'
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                    >
                      Invite New
                    </button>
                    <button
                      onClick={() => { setModalTab('transfer'); setInviteError(null); }}
                      className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        modalTab === 'transfer'
                          ? 'bg-white text-gray-800 shadow-sm dark:bg-gray-600 dark:text-white'
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                    >
                      Transfer Existing
                    </button>
                  </div>
                )}

                {modalTab === 'invite' ? (
                  /* Invite New Tab */
                  <>
                    <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                      {inviteType === 1
                        ? "Enter the email and name of the Site Manager. They will receive a 6-digit code to join."
                        : "Enter the name of the Floor Representative. An invite will be sent to the building email address."}
                    </p>

                    <div className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          type="text"
                          value={inviteName}
                          onChange={(e) => setInviteName(e.target.value)}
                          placeholder="Enter full name"
                        />
                      </div>

                      {inviteType === 1 && (
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="Enter email address"
                          />
                          <p className="mt-1 text-xs text-gray-500">Must be an @sbmnyc.com email address</p>
                        </div>
                      )}
                    </div>

                    {inviteError && (
                      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                        {inviteError}
                      </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                      <Button variant="outline" onClick={closeInviteModal} disabled={isInviting}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleInvite}
                        disabled={(inviteType === 1 && !inviteEmail) || !inviteName || isInviting}
                      >
                        {isInviting ? "Sending..." : "Send Invite"}
                      </Button>
                    </div>
                  </>
                ) : (
                  /* Transfer Existing Tab */
                  <div className="flex min-h-0 flex-1 flex-col">
                    <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                      Search for an existing {inviteType === 1 ? "Site Manager" : "Floor Representative"} to transfer to this building.
                    </p>

                    <div className="flex min-h-0 flex-1 flex-col space-y-4">
                      <div className="shrink-0">
                        <Label>Search by name or email</Label>
                        <Input
                          type="text"
                          value={searchKeyword}
                          onChange={(e) => setSearchKeyword(e.target.value)}
                          placeholder="Start typing to search..."
                        />
                      </div>

                      {/* Search Results */}
                      <div className="min-h-0 flex-1 overflow-y-auto">
                        {isSearching ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent"></div>
                            <span className="ml-2 text-sm text-gray-500">Searching...</span>
                          </div>
                        ) : searchResults.length > 0 ? (
                          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                            {searchResults.map((result) => (
                              <div key={result.id} className="flex items-center justify-between p-3">
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-medium text-gray-800 dark:text-white/90">{result.name}</p>
                                  <p className="truncate text-xs text-gray-500">
                                    {result.email || result.username}
                                  </p>
                                  <p className="truncate text-xs text-gray-400">
                                    Currently at: {result.building_name}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => handleTransfer(result.id, result.name)}
                                  disabled={isTransferring}
                                >
                                  {isTransferring ? "..." : "Transfer"}
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : searchKeyword.trim() && !isSearching ? (
                          <div className="py-4 text-center text-sm text-gray-500">
                            No {inviteType === 1 ? "site managers" : "floor representatives"} found matching "{searchKeyword}"
                          </div>
                        ) : null}
                      </div>

                      {/* Optional transfer note */}
                      {searchResults.length > 0 && (
                        <div>
                          <Label>Transfer Note (optional)</Label>
                          <Input
                            type="text"
                            value={transferNote}
                            onChange={(e) => setTransferNote(e.target.value)}
                            placeholder="Reason for transfer..."
                          />
                        </div>
                      )}
                    </div>

                    {transferError && (
                      <div className="mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                        {transferError}
                      </div>
                    )}

                    <div className="mt-6 flex shrink-0 justify-end">
                      <Button variant="outline" onClick={closeInviteModal} disabled={isTransferring}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Archive Site</h3>
            </div>

            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to archive <strong>{building.name}</strong>? This site will be hidden from the active sites list but can be reactivated later.
            </p>

            <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
              Type <strong>ARCHIVE</strong> to confirm:
            </p>

            <input
              type="text"
              value={archiveConfirmText}
              onChange={(e) => setArchiveConfirmText(e.target.value)}
              placeholder="Type ARCHIVE"
              className="mb-4 w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-700 dark:text-white"
            />

            {archiveError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {archiveError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveModal(false);
                  setArchiveConfirmText("");
                  setArchiveError(null);
                }}
                disabled={isArchiving}
              >
                Cancel
              </Button>
              <button
                onClick={handleArchive}
                disabled={archiveConfirmText !== "ARCHIVE" || isArchiving}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isArchiving ? "Archiving..." : "Archive Site"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
                <svg className="h-5 w-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                Edit {editFormData.user_type === 1 ? 'Site Manager' : 'Floor Representative'}
              </h3>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter name"
                />
              </div>

              {isAdmin && (
                <div>
                  <Label>Role</Label>
                  <select
                    value={String(editFormData.user_type)}
                    onChange={(e) => {
                      const newType = parseInt(e.target.value);
                      setEditFormData(prev => ({
                        ...prev,
                        user_type: newType,
                        // Clear credential fields when role changes
                        ...(newType !== editingUser!.user_type ? { email: '', username: '' } : {}),
                      }));
                    }}
                    className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-none focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  >
                    <option value="1">Site Manager</option>
                    <option value="2">Floor Representative</option>
                  </select>
                  {editFormData.user_type !== editingUser.user_type && (
                    <p className="mt-1 text-xs text-amber-600">
                      Changing role will force the user to re-login with new credentials
                    </p>
                  )}
                </div>
              )}

              {editFormData.user_type === 2 && (
                <div>
                  <Label>Username</Label>
                  <input
                    type="text"
                    value={editFormData.username}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Enter username"
                    autoComplete="off"
                    data-lpignore="true"
                    className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:focus:border-brand-800"
                  />
                </div>
              )}

              {editFormData.user_type === 1 && (
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                  />
                  <p className="mt-1 text-xs text-gray-500">Must be an @sbmnyc.com email address</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <Checkbox
                  label="Reset Password"
                  checked={resetPassword}
                  onChange={(checked) => setResetPassword(checked)}
                />
                {resetPassword && (
                  <div className="mt-3">
                    <Label>New Password</Label>
                    <Input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    <p className="mt-1 text-xs text-gray-500">User will be required to change this on next login</p>
                  </div>
                )}
              </div>
            </div>

            {editUserError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {editUserError}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeEditModal} disabled={isUpdatingUser}>
                Cancel
              </Button>
              <Button onClick={handleEditSave} disabled={isUpdatingUser || !editFormData.name}>
                {isUpdatingUser ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Archive User Confirmation Modal */}
      {showArchiveUserModal && archivingUser && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Archive User</h3>
            </div>

            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to archive <strong>{archivingUser.name}</strong>? They will be logged out, their access revoked, and they will no longer appear in this building's staff list. You can reactivate them later from Settings.
            </p>

            <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
              Type <strong>ARCHIVE</strong> to confirm:
            </p>

            <input
              type="text"
              value={archiveUserConfirmText}
              onChange={(e) => setArchiveUserConfirmText(e.target.value)}
              placeholder="Type ARCHIVE"
              className="mb-4 w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-700 dark:text-white"
            />

            {archiveUserError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {archiveUserError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeArchiveUserModal}
                disabled={isArchivingUser}
              >
                Cancel
              </Button>
              <button
                onClick={handleArchiveUser}
                disabled={archiveUserConfirmText !== "ARCHIVE" || isArchivingUser}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isArchivingUser ? "Archiving..." : "Archive User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer User to Building Modal */}
      {showUserTransferModal && transferringUser && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50">
          <div className="mx-4 flex h-[640px] w-full max-w-md flex-col rounded-2xl bg-white p-6 dark:bg-gray-800">
            {individualTransferSuccess ? (
              <>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Transfer Complete</h3>
                </div>
                <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">{individualTransferSuccess}</p>
                <div className="flex justify-end">
                  <Button onClick={closeUserTransferModal}>Done</Button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
                    <svg className="h-5 w-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Transfer {transferringUser.name}</h3>
                </div>

                <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                  Select a building to transfer <strong>{transferringUser.name}</strong> to.
                </p>

                <div className="flex min-h-0 flex-1 flex-col space-y-4">
                  <div className="shrink-0">
                    <Label>Search buildings</Label>
                    <Input
                      type="text"
                      value={buildingSearch}
                      onChange={(e) => setBuildingSearch(e.target.value)}
                      placeholder="Filter by building name..."
                    />
                  </div>

                  {/* Building Results */}
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {filteredBuildings.length > 0 ? (
                      <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                        {filteredBuildings.map((b) => (
                          <div key={b.id} className="flex items-center justify-between p-3">
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium text-gray-800 dark:text-white/90">{b.name}</p>
                              <p className="truncate text-xs text-gray-500">
                                {b.street_number} {b.street_name}{b.city ? `, ${b.city}` : ''}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleIndividualTransfer(b.id, b.name)}
                              disabled={isIndividualTransferring}
                            >
                              {isIndividualTransferring ? '...' : 'Transfer'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : allBuildings.length === 0 ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent"></div>
                        <span className="ml-2 text-sm text-gray-500">Loading buildings...</span>
                      </div>
                    ) : (
                      <div className="py-4 text-center text-sm text-gray-500">
                        No buildings found matching "{buildingSearch}"
                      </div>
                    )}
                  </div>

                  {/* Optional transfer note */}
                  <div className="shrink-0">
                    <Label>Transfer Note (optional)</Label>
                    <Input
                      type="text"
                      value={individualTransferNote}
                      onChange={(e) => setIndividualTransferNote(e.target.value)}
                      placeholder="Reason for transfer..."
                    />
                  </div>
                </div>

                {individualTransferError && (
                  <div className="mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    {individualTransferError}
                  </div>
                )}

                <div className="mt-6 flex shrink-0 justify-end">
                  <Button variant="outline" onClick={closeUserTransferModal} disabled={isIndividualTransferring}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
